import React, { useCallback, useEffect, useState } from 'react';
import { IoArrowDown } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import { Button, cn, Dropdown, Text } from 'rizzui';
import WidgetCard from '../../../components/card/WidgetCard';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import useFilters from '../../../hooks/useFilters';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { menuRoutes } from '../../../router/menu';
import { DashboardService } from '../../../services/dashboard/dashboardService';

interface ClientsSpendingProps {
    className?: string
}

interface ClientSpendingData {
    clientId: string;
    clientName: string;
    clientCifNif: string;
    totalSpent: string;
    contractCount: number;
}

interface ClientRankingProps {
    data: ClientSpendingData[];
}

const ClientRanking: React.FC<ClientRankingProps> = ({ data }) => {
    // Find the maximum contractCount to calculate the percentage for the progress bar
    const maxContracts = Math.max(...data.map(client => client.contractCount));

    // Order the data by contractCount in descending order
    const sortedData = [...data].sort((a, b) => b.contractCount - a.contractCount);

    return (
        <div className="space-y-3">
            {sortedData.map((client, index) => {
                // Calculate the percentage for the progress bar
                const percentage = maxContracts > 0 ? (client.contractCount / maxContracts) * 100 : 0;

                return (
                    <div key={client.clientId} className="space-y-2">
                        <div className="flex justify-between items-center">
                            <div className="flex-1">

                                <Link
                                    to={`${menuRoutes.clients.path}/${client.clientId}/profile${menuRoutes.clients.profile.info}`}
                                    className="text-primary font-semibold"
                                >
                                    #{index + 1} {client.clientName}
                                </Link>
                                <Text className="text-xs text-gray-500">
                                    CIF/NIF: {client.clientCifNif}
                                </Text>
                            </div>
                            <div className="text-right ml-2">
                                <Text className="font-semibold text-sm">
                                    {client.contractCount} {client.contractCount === 1 ? 'contrato' : 'contratos'}
                                </Text>
                                <Text className="text-xs text-gray-500">
                                    {parseFloat(client.totalSpent).toLocaleString()}€
                                </Text>
                            </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-secondary opacity-75 h-2 rounded-full transition-all duration-300 ease-in-out"
                                style={{ width: `${percentage}%` }}
                            ></div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const ClientsSpending: React.FC<ClientsSpendingProps> = ({ className }) => {
    const { filters } = useFiltersPR();
    const { filters: newFilters, updatePageSize } = useFilters({ option: "clients-with-higher-spending" }, [], 1, 5);
    const { handleErrors } = useHandleErrors();

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<ClientSpendingData[]>([]);

    const fetchOrders = useCallback(async (params: any) => {
        setIsLoading(true);
        try {
            const response = await new DashboardService().getClientsWithHigherSpending(params);
            const responseData = response.getResponseData()?.data;
            if (responseData) {
                setData(responseData);
            } else {
                handleErrors(response)
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [newFilters]);

    useEffect(() => {
        if (filters.filter_filters?.from && filters.filter_filters?.to) {
            fetchOrders({ filter_filters: { ...newFilters.filter_filters, ...filters.filter_filters }, limit: newFilters.limit });
        }
    }, [fetchOrders, newFilters, filters]);

    return (
        <WidgetCard
            className={cn('@container', className)}
            title="Ranking de clientes que más han comprado (contratos en estado satisfactorio)"
            filters={<Dropdown>
                <Dropdown.Trigger>
                    <Button
                        as="span"
                        variant="outline"
                    >
                        {newFilters.limit || 'Selecciona un límite'}
                        <IoArrowDown className="ml-2" />
                    </Button>
                </Dropdown.Trigger>
                <Dropdown.Menu>
                    <Dropdown.Item onClick={() => { updatePageSize({ value: 5 }) }} className='text-nowrap'>5</Dropdown.Item>
                    <Dropdown.Item onClick={() => { updatePageSize({ value: 10 }) }} className='text-nowrap'>10</Dropdown.Item>
                    <Dropdown.Item onClick={() => { updatePageSize({ value: 25 }) }} className='text-nowrap'>25</Dropdown.Item>
                    <Dropdown.Item onClick={() => { updatePageSize({ value: 50 }) }} className='text-nowrap'>50</Dropdown.Item>
                    <Dropdown.Item onClick={() => { updatePageSize({ value: 100 }) }} className='text-nowrap'>100</Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>}
        >
            {isLoading
                ? <div className="flex justify-center items-center h-32"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
                : (
                    <div className="pt-6 px-4">
                        {data.length > 0
                            ? <ClientRanking data={data} />
                            : <div className="text-center text-gray-500 py-8">No hay datos disponibles</div>
                        }
                    </div>
                )
            }
        </WidgetCard>
    );
};

export default ClientsSpending;